import { Box, FormControlLabel, InputLabel, Typography } from '@mui/material';
import { DIFFERENT_COLOR, TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import PieChartCommon from 'components/Common/PieChartCommon';
import SkeletonCharts from 'components/Common/SkeletonCharts';
import { SwitchCustom } from 'components/Common/SwitchCustom';
import { useClientSettingsStyle } from 'components/Dashboard/ClientSettings/ClientSettingsStyle';
import { QUERY_KEY } from 'constants/constants';
import { get, isEmpty } from 'lodash';
import randomColor from 'randomcolor';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  getLeafPhenologyFieldAnalytic,
  getSizeLeafPhenologyFieldAnalytic,
} from 'services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  changeSizeLeafPhenologyFieldList,
  mapViewSelector,
  setDataLeafPhenologyField,
} from 'store/slices/mapViewSlice';
import LeafPhenologyFieldCardLayer from '../../Layer/LeafPhenologyField';
import { SwipeableViewsCustom } from '../../SwipeableViewsCustom';
import InfoTable from '../CropCover/InfoTable';

const LeafPhenologyFieldAnalytics = () => {
  const dispatch = useAppDispatch();
  const [chartMode, setChartMode] = useState(false);
  const classes = useClientSettingsStyle();

  const { analyticId, sizeLeafPhenologyFieldSelected, dataLeafPhenologyField, sizeLeafPhenologyFieldList } =
    useAppSelector(mapViewSelector);

  useQuery(
    [QUERY_KEY.SIZE_LEAF_PHENOLOGY_FIELD_ANALYTIC, analyticId],
    () => getSizeLeafPhenologyFieldAnalytic(analyticId || ''),
    {
      enabled: !!analyticId && isEmpty(sizeLeafPhenologyFieldList),
      onSuccess: (response) => {
        if (response.data) {
          dispatch(changeSizeLeafPhenologyFieldList(response.data));
        }
      },
    }
  );

  const { data: dataCropType } = useQuery(
    [QUERY_KEY.LEAF_PHENOLOGY_FIELD_ANALYTIC, analyticId, sizeLeafPhenologyFieldSelected],
    () => getLeafPhenologyFieldAnalytic(analyticId || '', sizeLeafPhenologyFieldSelected + ''),
    { enabled: !!analyticId && !!sizeLeafPhenologyFieldSelected }
  );

  const dataPie = useMemo(() => {
    if (dataLeafPhenologyField) {
      return dataLeafPhenologyField.map((_item: any) => ({
        name: _item.type,
        value: _item.lduseArea,
        totalTree: _item.totalTree,
      }));
    }
    return [];
  }, [dataLeafPhenologyField]);

  const getDataChart = useMemo(
    () =>
      dataLeafPhenologyField?.map((_item: any) => ({
        name: _item.type,
        value: chartMode ? _item.totalTree : _item.lduseArea,
      })),
    [chartMode, dataLeafPhenologyField]
  );

  const hasTreeCount = !!(
    dataLeafPhenologyField?.length && dataLeafPhenologyField.some((item: any) => item.totalTree > 0)
  );

  const colorLeafPhenologyField = useMemo(() => {
    if (dataCropType?.data) {
      let colors: string[] = [];
      dispatch(
        setDataLeafPhenologyField(
          dataCropType.data.map((item: any, index: number) => {
            const colorRandom = randomColor({ luminosity: 'dark' });
            const _color = item.color || get(DIFFERENT_COLOR, `[${index}]`, colorRandom);
            colors.push(_color);
            return {
              ...item,
              visible: true,
              color: _color,
            };
          })
        )
      );
      return colors;
    }
    return [];
  }, [dataCropType, dispatch]);

  return (
    <SwipeableViewsCustom
      isDisableLabel
      analyticName={TYPE_ANALYTICS_MAP_VIEW.LEAF_PHENOLOGY_FIELD}
      analyticTab={
        <>
          {dataLeafPhenologyField?.length > 0 ? (
            <>
              <PieChartCommon
                title="Leaf Phenology Field"
                dataChart={getDataChart}
                colorCharts={colorLeafPhenologyField}
              />
              {hasTreeCount && (
                <>
                  <InputLabel sx={{ marginLeft: '25px', fontSize: '14px' }}>Chart Mode:</InputLabel>
                  <FormControlLabel
                    sx={{ marginLeft: '30px' }}
                    control={
                      <SwitchCustom
                        checked={chartMode}
                        onClick={() => {
                          setChartMode(!chartMode);
                        }}
                      />
                    }
                    label={<Typography className={classes.textSetting}>{chartMode ? 'Tree Count' : 'Area'}</Typography>}
                  />
                </>
              )}
              <Box component={'div'} sx={{ marginTop: '30px' }}>
                <InfoTable dataPie={dataPie} hasTreeCount={hasTreeCount} />
              </Box>
            </>
          ) : (
            <>
              <SkeletonCharts count={1} />
            </>
          )}
        </>
      }
      layerTab={<LeafPhenologyFieldCardLayer labelCard={TYPE_ANALYTICS_MAP_VIEW.LEAF_PHENOLOGY_FIELD} />}
      isShowDownloadButton={dataLeafPhenologyField?.length > 0}
    />
  );
};

export default LeafPhenologyFieldAnalytics;
